import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bug, ChevronLeft, SlidersHorizontal, Building2, Users, Gavel, Waypoints, Scale, Puzzle, Tag, UserRoundSearch, Eye, Link2, FolderOpen, CircleAlert, CalendarDays, X, Layers, Flag } from 'lucide-react';

import '../../components/Campaigns/Campaigns.css';
import '../../components/BugManagement/BugManagement.css';
import FilterSelect from '../../components/Campaigns/FilterSelect';
import BugsTable from '../../components/BugManagement/BugsTable';
import BugsToolbar from '../../components/BugManagement/BugsToolbar';
import DateRangePicker from '../../components/Filters/DateRangePicker';
import MatchesDialog from '../../components/BugManagement/MatchesDialog';
import StatsTab from '../../components/BugManagement/StatsTab';
import { LINK_CHECK_LABELS } from '../../components/BugManagement/duplicateLinks';
import useDuplicateTickets from '../../hooks/useDuplicateTickets';
import useDuplicateStats from '../../hooks/useDuplicateStats';
import useDuplicateLink from '../../hooks/useDuplicateLink';
import useDuplicateCandidates from '../../hooks/useDuplicateCandidates';
import useCampaigns from '../../hooks/useCampaigns';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { NONE_SENTINEL } from '../../components/Campaigns/filterConstants';

const LINK_CHECK_FILTER_VALUES = ['ok', 'missing'];
const RESOLUTION_FILTER_VALUES = [NONE_SENTINEL, 'Duplicate', 'Done'];

// Shared with QualityKpi.js's own copy of this pattern: `from`/`to` are stored as plain
// YYYY-MM-DD, not the preset name, so a shared link reproduces the exact same dates
// regardless of when it's opened (a preset like "30d" is relative to "today").
const formatDateParam = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDateParam = (s) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

// No default range: unlike a trend dashboard, this page's job is browsing the backlog —
// an aging unlinked duplicate from two years ago is exactly what it exists to surface, so
// defaulting to a rolling 30-day window the way QualityKpi.js does would hide it.
const EMPTY_DATE_RANGE = { startDate: null, endDate: null, preset: null };

function readDateRangeFromParams(searchParams) {
  const startDate = parseDateParam(searchParams.get('from'));
  const endDate = parseDateParam(searchParams.get('to'));
  // The exact preset that produced this range isn't recoverable from from/to alone, but the
  // filtered dates are unaffected — only which toggle button appears pre-selected is approximate.
  return startDate && endDate ? { startDate, endDate, preset: 'custom' } : EMPTY_DATE_RANGE;
}

const ico = (Icon) => <Icon size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />;

const FILTER_GROUPS = [
  {
    label: <>{ico(Bug)} Issue</>,
    filters: [
      { key: 'issueType', label: 'Issue Type', labelNode: <>{ico(Layers)}Issue Type</> },
      { key: 'project',    label: 'Project',    labelNode: <>{ico(FolderOpen)}Project</> },
      { key: 'status',     label: 'Status',     labelNode: <>{ico(Waypoints)}Status</> },
      { key: 'resolution', label: 'Resolution', labelNode: <>{ico(Gavel)}Resolution</> },
      { key: 'severity',   label: 'Severity',   labelNode: <>{ico(Scale)}Severity</> },
    ],
  },
  {
    label: <>{ico(Link2)} Duplicates</>,
    filters: [
      { key: 'linkState', label: 'Link check', labelNode: <>{ico(Link2)}Link check</> },
    ],
  },
  {
    label: <>{ico(Building2)} Organisation</>,
    filters: [
      { key: 'component', label: 'Component', labelNode: <>{ico(Puzzle)}Component</> },
      { key: 'unit',      label: 'Unit',      labelNode: <>{ico(Tag)}Unit</> },
      { key: 'campaign',  label: 'Campaign',  labelNode: <>{ico(Flag)}Campaign</> },
    ],
  },
  {
    label: <>{ico(Users)} People</>,
    filters: [
      { key: 'reporter', label: 'Reporter', labelNode: <>{ico(UserRoundSearch)}Reporter</> },
      { key: 'assignee', label: 'Assignee', labelNode: <>{ico(Eye)}Assignee</> },
    ],
  },
];

const FILTERS = FILTER_GROUPS.flatMap(g => g.filters);
const EMPTY_FILTERS = Object.fromEntries(FILTERS.map(f => [f.key, []]));
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const TABS = [
  { key: 'duplicates', label: 'Duplicates' },
  { key: 'stats',      label: 'Stats' },
];

// Multi-valued in Jira and stored comma-separated with stray quotes, same as the
// campaign issues table does it.
function splitMulti(value) {
  if (!value) return [];
  return value.split(',').map(v => v.trim().replace(/^"|"$/g, '')).filter(Boolean);
}

function bugFilterValues(bug, key, campaigns) {
  if (key === 'linkState') return [bug.linkState];
  if (key === 'component') return splitMulti(bug.component);
  if (key === 'campaign') {
    if (!bug.labels) return [];
    return campaigns.filter(c => bug.labels.includes(c.campaignLabel)).map(c => c.campaignLabel);
  }
  if (key === 'resolution') return [bug.resolution || NONE_SENTINEL];
  const v = bug[key];
  return v ? [v] : [];
}

function severityLabel(v) {
  const lower = v.toLowerCase();
  if (lower.includes('critical')) return '🚨 Critical and Blocker';
  if (lower.includes('major')) return '🔥 Major';
  if (lower.includes('minor')) return '🙁 Minor';
  return v;
}

export default function DuplicateTickets() {
  const jiraName = useCurrentUser()?.jiraName ?? null;
  const [searchParams, setSearchParams] = useSearchParams();

  // These are server-side parameters, so they are declared before the fetch that reads them.
  const [showAll, setShowAll] = useState(() => searchParams.get('all') === '1');
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [includeDesc, setIncludeDesc] = useState(() => searchParams.get('qd') === '1');
  // Defaults to visible (QA-7466) — the table's original purpose is browsing newly flagged,
  // not-yet-triaged potential duplicates, alongside confirmed duplicates missing their Jira link.
  const [showUntriaged, setShowUntriaged] = useState(() => searchParams.get('untriaged') !== '0');

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'duplicates');
  const [filters, setFilters] = useState(() => {
    const initial = { ...EMPTY_FILTERS, issueType: ['Bug'], resolution: ['Duplicate'] };
    FILTERS.forEach(f => { const v = searchParams.getAll(f.key); if (v.length) initial[f.key] = v; });
    return initial;
  });
  const { campaigns } = useCampaigns();
  const campaignNameByLabel = useMemo(
    () => new Map(campaigns.map(c => [c.campaignLabel, c.campaignName])),
    [campaigns]
  );

  // Mirrors the "Unlinked duplicates" quick-filter's own combination — however the user gets to
  // exactly this slice (the preset button or picking the same two filters by hand), broaden the
  // fetch to the real actionable backlog instead of just filtering the flagged-only one. Most of
  // it was never flagged by the matcher, so filtering client-side alone would hide ~75% of it —
  // see DuplicatesService.loadNeedsLinkingBugs.
  const needsLinking = filters.resolution?.length === 1 && filters.resolution[0] === 'Duplicate'
    && filters.linkState?.length === 1 && filters.linkState[0] === 'missing';

  const { data, loading, error } = useDuplicateTickets({
    includeWithoutMatches: showAll,
    needsLinking,
    q: search,
    searchDescription: includeDesc,
    includeUntriaged: showUntriaged
  });
  const { stats } = useDuplicateStats();
  const { link: linkDuplicate, linking } = useDuplicateLink();
  const [dateRange, setDateRange] = useState(() => readDateRangeFromParams(searchParams));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [toast, setToast] = useState(null);

  // Optimistic patch over the real API rows: keyed by bug key, populated from the real
  // POST /hub/duplicates/link response in handleLink so the row updates immediately without
  // waiting for a full refetch.
  const [edits, setEdits] = useState({});

  const handleFiltersChange = useCallback(v => setFilters(v), []);

  const bugs = useMemo(() => {
    if (!data) return [];
    return data.bugs.map(b => (edits[b.key] ? { ...b, ...edits[b.key] } : b));
  }, [data, edits]);

  // Rows surviving everything except one filter key — used both for the visible rows
  // (exceptKey = null) and for each filter's cross-filtered option counts.
  const rowsExcept = useCallback((exceptKey) => {
    const term = search.trim().toLowerCase();
    return bugs.filter(bug => {
      // Belt and braces: with showAll/needsLinking off the server already returns flagged bugs
      // only. needsLinking bugs legitimately have zero matches on purpose (the matcher never
      // flagged them) — that's the whole point of the broader fetch, not something to re-hide.
      if (!showAll && !needsLinking && bug.matches.length === 0) return false;

      // With "incl. description" on, the server has already resolved the term against key,
      // summary AND description — descriptions are not on these rows. Re-filtering here on
      // key/summary alone would silently drop the description-only matches it just found.
      if (term && !includeDesc) {
        const haystack = [bug.key, bug.summary].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      if (dateRange.startDate && dateRange.endDate) {
        // Same rule as every other filter here: a bug missing the value being filtered on
        // (here, no parseable created date) doesn't match an active filter — see bugFilterValues.
        const created = bug.created ? new Date(bug.created) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        const time = created.getTime();
        if (time < dateRange.startDate.getTime() || time >= dateRange.endDate.getTime() + ONE_DAY_MS) return false;
      }

      return FILTERS.every(f => {
        if (f.key === exceptKey) return true;
        const selected = filters[f.key];
        if (!selected?.length) return true;
        const values = bugFilterValues(bug, f.key, campaigns);
        return values.some(v => selected.includes(v));
      });
    });
  }, [bugs, filters, search, includeDesc, showAll, needsLinking, dateRange, campaigns]);

  const visibleBugs = useMemo(() => rowsExcept(null), [rowsExcept]);

  const filterOptions = useMemo(() => {
    const out = {};
    FILTERS.forEach(f => {
      const counts = new Map();
      rowsExcept(f.key).forEach(bug => {
        bugFilterValues(bug, f.key, campaigns).forEach(v => counts.set(v, (counts.get(v) ?? 0) + 1));
      });
      if (f.key === 'campaign') {
        campaigns.forEach(c => { if (!counts.has(c.campaignLabel)) counts.set(c.campaignLabel, 0); });
      }
      if (f.key === 'linkState') {
        out[f.key] = LINK_CHECK_FILTER_VALUES.map(value => ({ value, count: counts.get(value) ?? 0 }));
        return;
      }
      if (f.key === 'resolution') {
        out[f.key] = RESOLUTION_FILTER_VALUES.map(value => ({ value, count: counts.get(value) ?? 0 }));
        return;
      }
      out[f.key] = [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    });
    return out;
  }, [rowsExcept, campaigns]);

  // Sync state → URL so a filtered view can be shared
  React.useEffect(() => {
    const params = new URLSearchParams();
    FILTERS.forEach(f => filters[f.key].forEach(v => params.append(f.key, v)));
    if (search) params.set('q', search);
    if (includeDesc) params.set('qd', '1');
    if (showAll) params.set('all', '1');
    if (!showUntriaged) params.set('untriaged', '0');
    if (dateRange.startDate && dateRange.endDate) {
      params.set('from', formatDateParam(dateRange.startDate));
      params.set('to', formatDateParam(dateRange.endDate));
    }
    if (activeTab !== 'duplicates') params.set('tab', activeTab);
    setSearchParams(params, { replace: true });
  }, [filters, search, includeDesc, showAll, showUntriaged, dateRange, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedBug = selectedKey ? visibleBugs.find(b => b.key === selectedKey) ?? bugs.find(b => b.key === selectedKey) : null;

  // On-demand candidate detail (QA-7428) — only fetched while the dialog for `selectedKey` is
  // open, since this is the endpoint that includes descriptions the main list response omits.
  const { data: candidates, loading: candidatesLoading, error: candidatesError, refetch: refetchCandidates } =
    useDuplicateCandidates(selectedKey);

  async function handleLink(targetKey) {
    if (!selectedBug) return;
    const bugKey = selectedBug.key;
    try {
      const result = await linkDuplicate(bugKey, targetKey);
      setEdits(prev => ({
        ...prev,
        [bugKey]: { ...(prev[bugKey] ?? {}), linkState: result.linkState, duplicateLinks: result.duplicateLinks },
      }));
      setToast(`${bugKey} linked to ${targetKey} in Jira.`);
      // Reflects the latest link state in the dialog without waiting for it to be reopened.
      refetchCandidates();
    } catch (err) {
      setToast(`Couldn't link ${bugKey} to ${targetKey}: ${err.response?.data?.message || err.message}`);
    }
  }

  React.useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  return (
    <div className="application-container campaign-page">

      <div className="campaign-header">
        <div className="campaign-header-center">
          <h1>Duplicate Tickets</h1>
          <p className="campaign-dates">
            Bugs with potential duplicates found by the embedding matcher
            {data ? <> · similarity ≥ {data.threshold} · up to {data.maxMatches} matches</> : null}
          </p>
        </div>
      </div>

      {toast && (
        <div className="dup-toast"><CircleAlert size={14} />{toast}</div>
      )}

      <div className="campaign-body">

        <div className={`campaign-sidebar-container right${sidebarCollapsed ? ' collapsed' : ''}`}>
          <button
            className={`sidebar-toggle-button right${sidebarCollapsed ? ' collapsed' : ''}`}
            onClick={() => setSidebarCollapsed(c => !c)}
            aria-label="Toggle filters"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="campaign-sidebar">
            <span className="campaign-sidebar-collapsed-label"><SlidersHorizontal size={20} /></span>
            <div className="sidebar-filters-content">
              <div className="sidebar-title">
                <SlidersHorizontal size={16} style={{ marginRight: 5, verticalAlign: 'middle', flexShrink: 0 }} />
                Filters
              </div>
              <div className="filter-item">
                <label className="dup-date-range-label">
                  <span>{ico(CalendarDays)}Date Range</span>
                  {dateRange.startDate && (
                    <button
                      type="button"
                      className="dup-date-range-clear"
                      onClick={() => setDateRange(EMPTY_DATE_RANGE)}
                      aria-label="Clear date range"
                      title="Clear date range"
                    >
                      <X size={12} />
                    </button>
                  )}
                </label>
                <DateRangePicker
                  value={dateRange}
                  preset={dateRange.preset}
                  onChange={setDateRange}
                  enabledPresets={['30d', '90d', '6m', '12m', 'custom']}
                  mode="dialog"
                />
              </div>
              {FILTER_GROUPS.map((group, gi) => (
                <div key={gi} className="filter-group">
                  <div className="filter-group-label">{group.label}</div>
                  {group.filters.map(f => (
                    <div key={f.key} className="filter-item">
                      <label>{f.labelNode ?? f.label}</label>
                      <FilterSelect
                        options={filterOptions[f.key] ?? []}
                        value={filters[f.key]}
                        disabled={loading}
                        onChange={v => setFilters(prev => ({ ...prev, [f.key]: v }))}
                        formatLabel={
                          f.key === 'severity' ? severityLabel
                          : f.key === 'linkState' ? (v => LINK_CHECK_LABELS[v] ?? v)
                          : f.key === 'campaign' ? (v => campaignNameByLabel.get(v) ?? v)
                          : f.key === 'resolution' ? (v => v === NONE_SENTINEL ? 'No resolution' : v)
                          : undefined
                        }
                      />
                    </div>
                  ))}
                </div>
              ))}
              <div style={{ height: 40 }} />
            </div>
          </div>
        </div>

        <div className="campaign-main">
          <div className="campaign-tabs">
            {TABS.map(t => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="tab-panel">
            {activeTab === 'duplicates' && (
              loading ? <div className="section-loading">Loading…</div>
              : error ? <div className="section-loading">Could not load duplicates: {error.message}</div>
              : (
                <>
                  <BugsToolbar
                    count={visibleBugs.length}
                    // The bug population, from the API — the rows only cover flagged bugs, so
                    // "1444 of N have matches" cannot be derived from what was sent.
                    totalCount={data?.totalBugs}
                    search={search}
                    onSearchChange={setSearch}
                    includeDesc={includeDesc}
                    onIncludeDescChange={setIncludeDesc}
                    showAll={showAll}
                    onShowAllChange={setShowAll}
                    showUntriaged={showUntriaged}
                    onShowUntriagedChange={setShowUntriaged}
                    jiraName={jiraName}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    emptyFilters={EMPTY_FILTERS}
                    onToggleColumns={() => setShowColumnSelector(v => !v)}
                  />
                  <BugsTable
                    bugs={visibleBugs}
                    maxMatches={data?.maxMatches}
                    showColumnSelector={showColumnSelector}
                    onCloseColumnSelector={() => setShowColumnSelector(false)}
                    onOpenMatches={bug => setSelectedKey(bug.key)}
                  />
                </>
              )
            )}

            {activeTab === 'stats' && (
              <StatsTab stats={stats} sessionLinkedKeys={Object.keys(edits)} />
            )}
          </div>
        </div>
      </div>

      {selectedBug && (
        <MatchesDialog
          bug={selectedBug}
          onClose={() => setSelectedKey(null)}
          onLink={handleLink}
          linking={linking}
          candidates={candidates}
          candidatesLoading={candidatesLoading}
          candidatesError={candidatesError}
        />
      )}
    </div>
  );
}
